import React from 'react';
import { EVENT_FAQ_CONST, EVENT_FAQ_ANSWERS_CONST, FOOD_BEVERAGE_PROPS, ALCOHOL_PROPS, FURNITURE_PROPS, RESTROOM_PROPS, AV_MUSIC_PROPS, EVENT_ACTIVITIES_PROPS, EVENT_RULES_PROPS } from 'shared-components/faq/data/constants';
import { miscEventFaqDefinitions } from '../data/eventFaqDefinition';
import { QuestionFilled } from '../../icons';
import { FormattedMessage } from 'react-intl';
import Popover from 'react-bootstrap/lib/Popover';
import { OverlayTrigger } from 'react-bootstrap';
import EventFAQContext from './EventFAQContext';

const toolTipConverter = {
  standard_restrooms: 'restroom',
  cups_glasses: 'cups',
  place_setting: 'place_setting'
};

const renderEntryTitle = (messagedObj, { quantity } = {}, amenity) => {
  const toolTipAvailable = miscEventFaqDefinitions[`${amenity}_tooltip`] || miscEventFaqDefinitions[`${toolTipConverter[amenity]}_tooltip`];
  return (
    <EventFAQContext.Consumer>
      {(value) => (<div className="faq-label flex middle">
        <div className={quantity ? 'amenity-description ellipsis' : '' }>
          <FormattedMessage {...messagedObj} />
        </div>
        {quantity && <><span className="text-gray-300">
          <span className="bull num-guests-bull margin-sm-left margin-sm-right">•</span>
        </span><span>Qty: {quantity}</span></>}
        {toolTipAvailable &&
          <div className="margin-sm-left icon-adjust">
            <OverlayTrigger
              trigger="click"
              placement="top"
              rootClose
              overlay={
                <Popover
                  id="popover-positioned-top"
                  style={{ zIndex: value?.popOverIndex || 1029, borderRadius: '2px' }}
                  className="white-bg text-gray-darker"
                >
                  <FormattedMessage id={toolTipAvailable.id} defaultMessage={toolTipAvailable.defaultMessage} />
                </Popover>
              }
            >
              <div role="button" className="flex test">
                <QuestionFilled className="text-gray-500" />
              </div>
            </OverlayTrigger>
          </div>
        }
      </div>)}
    </EventFAQContext.Consumer>);
};

const alcoholRules = (data) => {
  const { BRING_ALCOHOL, BRING_ALCOHOL_RULES, ALCOHOL_MUST_BE_SERVED_BY_LICENSED_VENDOR, CAN_PURCHASE_ALCOHOL_AT_FULL_SERVICE_BAR, CAN_PURCHASE_BEER_WINE_AT_BAR, OTHER_ALCOHOL_CONSUMPTION_RULES } = EVENT_FAQ_CONST;
  const alcoholRuleArr = [ALCOHOL_MUST_BE_SERVED_BY_LICENSED_VENDOR, CAN_PURCHASE_ALCOHOL_AT_FULL_SERVICE_BAR, CAN_PURCHASE_BEER_WINE_AT_BAR ];
  const { BRING_ALCOHOL_ALLOWED, BRING_ALCOHOL_NOT_ALLOWED, ANY_ALCOHOL } = EVENT_FAQ_ANSWERS_CONST;
  const alcoholRule = data.hasOwnProperty(BRING_ALCOHOL) ? data[BRING_ALCOHOL] : BRING_ALCOHOL_ALLOWED;
  let moreInformation;
  let crossedOutRules = [];
  if (BRING_ALCOHOL_ALLOWED === alcoholRule) {
    moreInformation = data.hasOwnProperty(BRING_ALCOHOL_RULES) ? data[BRING_ALCOHOL_RULES] : ANY_ALCOHOL;
  } else if (BRING_ALCOHOL_NOT_ALLOWED === alcoholRule) {
    moreInformation = alcoholRuleArr.filter((rule) => data.hasOwnProperty(rule) && data[rule]);
    crossedOutRules = alcoholRuleArr.filter((rule) => data.hasOwnProperty(rule) && !data[rule]);
  }
  return {
    alcoholRule,
    moreInformation,
    crossedOutRules
  };
};

const AVOptionsSelected = (data) => {
  const { MICROPHONES, PROJECTOR, SPEAKERS, TELEVISION } = EVENT_FAQ_CONST;
  const allAVOptions = [MICROPHONES, PROJECTOR, SPEAKERS, TELEVISION];
  return allAVOptions.filter(activity => data.hasOwnProperty(activity));
};

const AVRulesSelected = (data) => {
  const { IS_DJ_ALLOWED, IS_LIVE_MUSIC_ALLOWED, IS_AMPLIFIED_MUSIC_ALLOWED } = EVENT_FAQ_CONST;
  const allAVRules = [IS_DJ_ALLOWED, IS_LIVE_MUSIC_ALLOWED, IS_AMPLIFIED_MUSIC_ALLOWED];
  return allAVRules.filter(activity => data.hasOwnProperty(activity) && data[activity]);
};

const AVRulesExplicitlyProhibited = (data) => {
  const { IS_DJ_ALLOWED, IS_LIVE_MUSIC_ALLOWED, IS_AMPLIFIED_MUSIC_ALLOWED } = EVENT_FAQ_CONST;
  const allAVRules = [IS_DJ_ALLOWED, IS_LIVE_MUSIC_ALLOWED, IS_AMPLIFIED_MUSIC_ALLOWED];
  return allAVRules.filter(activity => data.hasOwnProperty(activity) && !data[activity]);
};

const eventActivitiesSelected = (data) => {
  const { ARCADE_GAMES, DARTS, KARAOKE, PING_PONG_TABLE, POOL_TABLE, SHUFFLEBOARD } = EVENT_FAQ_CONST;
  const allActivities = [ARCADE_GAMES, DARTS, KARAOKE, PING_PONG_TABLE, POOL_TABLE, SHUFFLEBOARD];
  return allActivities.filter(activity => data.hasOwnProperty(activity));
};

const eventRulesSelected = (data) => {
  const { IS_GLITTER_ALLOWED, IS_HIRED_SECURITY_REQUIRED, ARE_OPEN_FLAMES_ALLOWED, ARE_PETS_ALLOWED, IS_SMOKING_ALLOWED, ARE_TICKETED_EVENTS_ALLOWED } = EVENT_FAQ_CONST;
  const allEventRules = [IS_GLITTER_ALLOWED, IS_HIRED_SECURITY_REQUIRED, ARE_OPEN_FLAMES_ALLOWED, ARE_PETS_ALLOWED, IS_SMOKING_ALLOWED, ARE_TICKETED_EVENTS_ALLOWED];
  return allEventRules.filter(rule => data.hasOwnProperty(rule) && data[rule]);
};

const eventRulesExplicitlyProhibited = (data) => {
  const { IS_GLITTER_ALLOWED, IS_HIRED_SECURITY_REQUIRED, ARE_OPEN_FLAMES_ALLOWED, ARE_PETS_ALLOWED, IS_SMOKING_ALLOWED, ARE_TICKETED_EVENTS_ALLOWED } = EVENT_FAQ_CONST;
  const allEventRules = [IS_GLITTER_ALLOWED, IS_HIRED_SECURITY_REQUIRED, ARE_OPEN_FLAMES_ALLOWED, ARE_PETS_ALLOWED, IS_SMOKING_ALLOWED, ARE_TICKETED_EVENTS_ALLOWED];
  return allEventRules.filter(rule => data.hasOwnProperty(rule) && !data[rule]);
};

const restroomsSelected = (data) => {
  const { STANDARD_RESTROOMS, ADA_RESTROOMS, NURSING_ROOMS } = EVENT_FAQ_CONST;
  const restroomOptions = [STANDARD_RESTROOMS, ADA_RESTROOMS, NURSING_ROOMS];
  return {
    selectedRestrooms: restroomOptions.filter(restroom => data.hasOwnProperty(restroom)),
    notSelectedRestrooms: restroomOptions.filter(restroom => !data.hasOwnProperty(restroom))
  };
};

const kitchensSelected = (data) => {
  const { FULL_KITCHEN, COMMERCIAL_KITCHEN, KITCHENETTE, PREP_AREA } = EVENT_FAQ_CONST;
  const kitchenSetups = [FULL_KITCHEN, COMMERCIAL_KITCHEN, KITCHENETTE, PREP_AREA];
  return kitchenSetups.filter(kitchen => data.hasOwnProperty(kitchen));
};

const furnitureSelected = (data) => {
  const { TABLES, CHAIRS, SOFT_SEATING } = EVENT_FAQ_CONST;
  const furnitureTypes = [TABLES, CHAIRS, SOFT_SEATING];
  return furnitureTypes.filter(furniture => data.hasOwnProperty(furniture));
};

const activitiesNotAllowed = (data) => {
  const { IS_21ST_BDAY_PROHIBITED,
    IS_AFTER_PARTY_PROHIBITED,
    IS_ALBUM_RELEASE_PARTY_PROHIBITED,
    IS_BACHELOR_PARTY_PROHIBITED,
    IS_CONCERT_PROHIBITED,
    IS_FESTIVAL_HOLIDAY_PARTY_PROHIBITED,
    IS_INFLUENCER_EVENT_PROHIBITED,
    IS_KIDS_PARTY_PROHIBITED,
    IS_SPORTS_VIEWING_PARTY_PROHIBITED,
    IS_PARTY_PROHIBITED } = EVENT_FAQ_CONST;
  const allActivitiesNotAllowed = [IS_21ST_BDAY_PROHIBITED,
    IS_AFTER_PARTY_PROHIBITED,
    IS_ALBUM_RELEASE_PARTY_PROHIBITED,
    IS_BACHELOR_PARTY_PROHIBITED,
    IS_CONCERT_PROHIBITED,
    IS_FESTIVAL_HOLIDAY_PARTY_PROHIBITED,
    IS_INFLUENCER_EVENT_PROHIBITED,
    IS_KIDS_PARTY_PROHIBITED,
    IS_SPORTS_VIEWING_PARTY_PROHIBITED,
    IS_PARTY_PROHIBITED];
  return allActivitiesNotAllowed.filter(activity => data.hasOwnProperty(activity) && data[activity]);
};

const displaySections = {
  displayFoodBeverageSection: (data) => FOOD_BEVERAGE_PROPS.some(key => data.hasOwnProperty(key)),
  displayAlcoholSection: (data) => ALCOHOL_PROPS.some(key => data.hasOwnProperty(key)),
  displayFurnitureSection: (data) => FURNITURE_PROPS.some(key => data.hasOwnProperty(key)),
  displayRestroomSection: (data) => RESTROOM_PROPS.some(key => data.hasOwnProperty(key)),
  displayAVMusicSection: (data) => AV_MUSIC_PROPS.some(key => data.hasOwnProperty(key)),
  displayEventActivitiesSection: (data) => EVENT_ACTIVITIES_PROPS.some(key => data.hasOwnProperty(key) && data[key]),
  displayEventRulesSection: (data) => EVENT_RULES_PROPS.some(key => data.hasOwnProperty(key))
};

export {
  renderEntryTitle,
  alcoholRules,
  eventActivitiesSelected,
  activitiesNotAllowed,
  AVOptionsSelected,
  AVRulesSelected,
  AVRulesExplicitlyProhibited,
  eventRulesSelected,
  eventRulesExplicitlyProhibited,
  restroomsSelected,
  kitchensSelected,
  furnitureSelected,
  displaySections
};
